import { z } from 'zod';

/**
 * The three routes that mint, renew and end a session - COW-483.
 *
 * Separate from `session.ts`, which answers "who is the caller" (`GET /v1/me`).
 * This file is about how a caller comes to have a token at all.
 *
 * **The refresh token never reaches page script.** Our API exchanges it and
 * stores it in a cookie the browser will not show to JavaScript, so the SPA
 * still holds only a fifteen-minute access token in memory - which is what
 * AUTH-004 decided, and this does not reverse it. What changes is that a reload
 * no longer loses the session, because what survives it is a cookie rather than
 * a variable. See ADR-0039.
 *
 * All three sit **outside the gateway's JWT authorizer**. They are how a caller
 * obtains a token, so requiring one would make them unreachable.
 */

/**
 * The cookie's name and path.
 *
 * Shared because three things must agree and none can see the others: the route
 * that sets it, the route that reads it, and `api-stack.ts`, whose CORS
 * configuration decides whether a browser will send it at all.
 *
 * `Path` is the narrowest that still covers all three routes. A cookie is sent
 * on every request beneath its path, so widening it would attach a credential
 * to every API call for no reason at all.
 *
 * **The SPA must send `credentials: 'include'`** on all three, or the browser
 * ignores `Set-Cookie` entirely and the request still returns 201 - a silent
 * success that leaves no session behind. Stated here because this is the file
 * the next author reads.
 */
export const SESSION_COOKIE_NAME = 'coworker_session';
export const SESSION_COOKIE_PATH = '/v1/session';

/**
 * Required on every session request, and it must carry exactly this value.
 *
 * **This is the CSRF defence, and it is not decoration.** A cross-site form or
 * image cannot set a custom header, so requiring one forces a preflight - and
 * the preflight is refused for any origin outside `allowedOrigins`.
 *
 * It is the **second** layer, not the only one. The cookie is `SameSite=Lax`,
 * which already refuses a cross-site POST; the header is what stops a request
 * from an origin that CORS does allow. An earlier draft used `SameSite=None` so
 * that `http://localhost:4200` would get a session too, and a STRICT review
 * showed that handed that origin - a credentialed one, since it is in
 * `allowedOrigins` - the ability to refresh somebody's session. See ADR-0039.
 */
export const SESSION_CSRF_HEADER = 'x-coworker-session';
export const SESSION_CSRF_VALUE = '1';

/**
 * How long the cookie lives.
 *
 * The ticket asks for a day. Cognito's refresh token is valid for thirty
 * (`auth-stack.ts`), so this is the binding limit and the one that has to be
 * stated: the session lasts a day because this says so, not because Cognito
 * stops honouring the token.
 */
export const SESSION_COOKIE_MAX_AGE_SECONDS = 24 * 60 * 60;

/**
 * `POST /v1/session` - redeem an authorization code.
 *
 * The verifier and the state stay a *tab* concern and are checked in the browser
 * before this is called. The server cannot know which tab began a login, and
 * inventing a server-side store for it would be a session before the session.
 */
export const createSessionRequestSchema = z
  .object({
    /** From Cognito's redirect back to `CALLBACK_PATH`. */
    code: z.string().min(1),
    /** The PKCE verifier the browser generated for this login. */
    codeVerifier: z.string().min(1),
    /**
     * Echoed to Cognito, which requires it to match the one that began the
     * flow. Sent rather than derived because the API serves two origins - the
     * deployed SPA and local development - and must not guess which one called.
     */
    redirectUri: z.url(),
  })
  .readonly();

export type CreateSessionRequest = z.infer<typeof createSessionRequestSchema>;

/**
 * What the SPA is given back.
 *
 * **There is no `refreshToken` field, and that absence is the contract.** A
 * response shape that could carry one is a shape somebody eventually fills in.
 * `packages/contracts` is isomorphic, so this is the same object the browser
 * parses with: a server that started returning a refresh token would fail here
 * rather than quietly succeed.
 */
export const sessionResponseSchema = z
  .object({
    accessToken: z.string().min(1),
    idToken: z.string().min(1),
    /** Seconds, as Cognito reports it. The SPA turns it into an instant itself. */
    expiresIn: z.number().int().positive(),
  })
  .readonly();

export type SessionResponse = z.infer<typeof sessionResponseSchema>;
